'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  BodyDefault,
  MetaDefault,
  RadioGroup,
  VirtualizedStack,
} from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import {
  ASSET_MERGE_NEW_SURVIVOR_VALUE,
  type AssetMergeCandidate,
} from '../../utils/asset-merge/asset-merge-selection';
import { AssetMergeSurvivorOption } from './AssetMergeSurvivorOption';
import { AssetMergeSurvivorPickButton } from './AssetMergeSurvivorPickButton';

export const ASSET_MERGE_SURVIVOR_VIRTUALIZE_FROM = 20;

const VIRTUALIZED_LIST_HEIGHT = '320px';

type AssetMergeSurvivorListProps = {
  assets: readonly AssetMergeCandidate[];
  suggestedId: string | undefined;
  selectedValue: string;
  onSelectValue: (value: string) => void;
};

export function AssetMergeSurvivorList({
  assets,
  suggestedId,
  selectedValue,
  onSelectValue,
}: AssetMergeSurvivorListProps) {
  const t = useTranslations('proxy.assets.merge');
  const isVirtualized = assets.length > ASSET_MERGE_SURVIVOR_VIRTUALIZE_FROM;
  const selectedAsset = useMemo(() => {
    if (
      selectedValue.length === 0 ||
      selectedValue === ASSET_MERGE_NEW_SURVIVOR_VALUE
    ) {
      return undefined;
    }

    return assets.find((asset) => asset.id === selectedValue);
  }, [assets, selectedValue]);
  const virtualizedAssets = useMemo(() => {
    if (!selectedAsset) {
      return assets;
    }

    return assets.filter((asset) => asset.id !== selectedAsset.id);
  }, [assets, selectedAsset]);
  const renderItem = useCallback(
    ({ item }: { item: AssetMergeCandidate }) => (
      <Box pb="2" w="full">
        <AssetMergeSurvivorPickButton
          asset={item}
          isSuggested={item.id === suggestedId}
          onSelect={onSelectValue}
        />
      </Box>
    ),
    [onSelectValue, suggestedId],
  );
  const computeItemKey = useCallback(
    ({ item }: { item: AssetMergeCandidate }) => item.id,
    [],
  );

  return (
    <>
      <MetaDefault color="fg.muted">{t('chooseSurvivor')}</MetaDefault>
      <RadioGroup.Root
        w="full"
        value={selectedValue.length > 0 ? selectedValue : undefined}
        onValueChange={(details) => {
          if (details.value) {
            onSelectValue(details.value);
          }
        }}
      >
        <VStack gap="2" alignItems="stretch" w="full">
          {isVirtualized ? (
            <>
              {selectedAsset ? (
                <AssetMergeSurvivorOption
                  asset={selectedAsset}
                  isSuggested={selectedAsset.id === suggestedId}
                />
              ) : null}
              <Box w="full" h={VIRTUALIZED_LIST_HEIGHT} minH={0}>
                <VirtualizedStack<AssetMergeCandidate>
                  data={virtualizedAssets}
                  computeItemKey={computeItemKey}
                  renderItem={renderItem}
                />
              </Box>
            </>
          ) : (
            assets.map((asset) => (
              <AssetMergeSurvivorOption
                key={asset.id}
                asset={asset}
                isSuggested={asset.id === suggestedId}
              />
            ))
          )}

          <RadioGroup.Item
            value={ASSET_MERGE_NEW_SURVIVOR_VALUE}
            w="full"
            minW={0}
          >
            <RadioGroup.ItemControl flexShrink={0} />
            <RadioGroup.ItemText>
              <BodyDefault>{t('mergeIntoNew')}</BodyDefault>
            </RadioGroup.ItemText>
            <RadioGroup.ItemHiddenInput />
          </RadioGroup.Item>
        </VStack>
      </RadioGroup.Root>
    </>
  );
}
